import { debounce } from 'lodash';
import { useEffect } from 'react';
import { EMITTER_CONSTANTS, KEY_RECOIL, TAB_KEY } from '@/shared/config';
import { handleRedoGroupedByKey, handleUndoGroupedByKey, setIsUndoRedo } from '@/shared/lib';
import emitter from '@/shared/lib/eventEmitter';
import { useReportStore } from '@/entities/report';
import { defaultBeatsOptions } from '@/entities/beat';

export type UndoHistoryEntry = {
  key: string | number;
  value: {
    value: any;
    valueOther: any;
    positions: any;
  };
  type: string | number;
  index: number;
  maxIndex: number;
};

/**
 * Listens for undo and redo keyboard shortcuts and replays the recorded history back into the report store. Binds a debounced keydown handler for Ctrl or Cmd with Z and Y, and wires per-tab setters for event, beat, grouped event, strip, ECG disclosure, template, daily summary and bookmark state that the history mapper writes through.
 *
 * @returns Nothing; this hook only runs the keyboard-listening effect.
 */
const useUndoRedoListener = () => {
  const setEventNewsAction = useReportStore((s) => s.actions.setEventNews);
  /**
   * Restores the newly added events recorded under the TAB_1 store key. Forwards the value, or an updater for it, to the report store event news action bound to that key.
   *
   * @param v - The new events to restore, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setEventNewsStateTab1 = (v: Parameters<typeof setEventNewsAction>[1]) =>
    setEventNewsAction(KEY_RECOIL.BEAT_HR, v);
  /**
   * Restores the newly added events recorded under the TAB_2 store key. Forwards the value, or an updater for it, to the report store event news action bound to that key.
   *
   * @param v - The new events to restore, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setEventNewsStateTab2 = (v: Parameters<typeof setEventNewsAction>[1]) =>
    setEventNewsAction(KEY_RECOIL.RHYTHM_EVENT, v);
  /**
   * Restores the newly added events recorded under the TAB_6 store key. Forwards the value, or an updater for it, to the report store event news action bound to that key.
   *
   * @param v - The new events to restore, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setEventNewsStateTab6 = (v: Parameters<typeof setEventNewsAction>[1]) =>
    setEventNewsAction(KEY_RECOIL.HR_SUMMARY, v);
  const setEventChangesAction = useReportStore((s) => s.actions.setEventChanges);
  /**
   * Restores the pending event edits recorded under the TAB_1 store key. Forwards the value, or an updater for it, to the report store event changes action bound to that key.
   *
   * @param v - The event changes to restore, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setEventChangesStateTab1 = (v: Parameters<typeof setEventChangesAction>[1]) =>
    setEventChangesAction(KEY_RECOIL.BEAT_HR, v);
  /**
   * Restores the pending event edits recorded under the TAB_2 store key. Forwards the value, or an updater for it, to the report store event changes action bound to that key.
   *
   * @param v - The event changes to restore, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setEventChangesStateTab2 = (v: Parameters<typeof setEventChangesAction>[1]) =>
    setEventChangesAction(KEY_RECOIL.RHYTHM_EVENT, v);
  /**
   * Restores the pending event edits recorded under the TAB_6 store key. Forwards the value, or an updater for it, to the report store event changes action bound to that key.
   *
   * @param v - The event changes to restore, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setEventChangesStateTab6 = (v: Parameters<typeof setEventChangesAction>[1]) =>
    setEventChangesAction(KEY_RECOIL.HR_SUMMARY, v);
  const setEventOthersAction = useReportStore((s) => s.actions.setEventOthers);
  /**
   * Restores the other-event edits recorded under the TAB_1 store key. Forwards the value, or an updater for it, to the report store event others action bound to that key.
   *
   * @param v - The other-event edits to restore, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setEventOthersStateTab1 = (v: Parameters<typeof setEventOthersAction>[1]) =>
    setEventOthersAction(KEY_RECOIL.BEAT_HR, v);
  /**
   * Restores the other-event edits recorded under the TAB_2 store key. Forwards the value, or an updater for it, to the report store event others action bound to that key.
   *
   * @param v - The other-event edits to restore, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setEventOthersStateTab2 = (v: Parameters<typeof setEventOthersAction>[1]) =>
    setEventOthersAction(KEY_RECOIL.RHYTHM_EVENT, v);
  /**
   * Restores the other-event edits recorded under the TAB_6 store key. Forwards the value, or an updater for it, to the report store event others action bound to that key.
   *
   * @param v - The other-event edits to restore, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setEventOthersStateTab6 = (v: Parameters<typeof setEventOthersAction>[1]) =>
    setEventOthersAction(KEY_RECOIL.HR_SUMMARY, v);
  const setBeatChangesAction = useReportStore((s) => s.actions.setBeatChanges);
  /**
   * Restores the pending beat edits recorded under the TAB_1 store key. Forwards the value, or an updater for it, to the report store beat changes action bound to that key.
   *
   * @param v - The beat changes to restore, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setBeatChangesStateTab1 = (v: Parameters<typeof setBeatChangesAction>[1]) =>
    setBeatChangesAction(KEY_RECOIL.BEAT_HR, v);
  /**
   * Restores the pending beat edits recorded under the TAB_2 store key. Forwards the value, or an updater for it, to the report store beat changes action bound to that key.
   *
   * @param v - The beat changes to restore, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setBeatChangesStateTab2 = (v: Parameters<typeof setBeatChangesAction>[1]) =>
    setBeatChangesAction(KEY_RECOIL.RHYTHM_EVENT, v);
  /**
   * Restores the pending beat edits recorded under the TAB_6 store key. Forwards the value, or an updater for it, to the report store beat changes action bound to that key.
   *
   * @param v - The beat changes to restore, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setBeatChangesStateTab6 = (v: Parameters<typeof setBeatChangesAction>[1]) =>
    setBeatChangesAction(KEY_RECOIL.HR_SUMMARY, v);
  const setGroupEventChangesAction = useReportStore((s) => s.actions.setGroupEventChanges);
  /**
   * Restores the grouped event changes recorded under the TAB_1 store key. Forwards the value, or an updater for it, to the report store group event changes action bound to that key.
   *
   * @param v - The grouped event changes to restore, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setGroupEventChanges1 = (v: Parameters<typeof setGroupEventChangesAction>[1]) =>
    setGroupEventChangesAction(KEY_RECOIL.BEAT_HR, v);
  /**
   * Restores the grouped event changes recorded under the TAB_2 store key. Forwards the value, or an updater for it, to the report store group event changes action bound to that key.
   *
   * @param v - The grouped event changes to restore, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setGroupEventChanges2 = (v: Parameters<typeof setGroupEventChangesAction>[1]) =>
    setGroupEventChangesAction(KEY_RECOIL.RHYTHM_EVENT, v);
  const setEcgDisclosureChange = useReportStore((s) => s.actions.setEcgDisclosureChange);
  const setChangeStripData = useReportStore((s) => s.actions.setChangeStripData);
  /**
   * Restores the strip event changes recorded under the TAB_3 store key. Forwards the value, or an updater for it, to the report store change strip data action bound to that key.
   *
   * @param v - The strip event changes to restore, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setStripEventChangesStateAI = (v: Parameters<typeof setChangeStripData>[1]) =>
    setChangeStripData(KEY_RECOIL.STRIP_MANAGEMENT, v);
  /**
   * Restores the strip event changes recorded under the TAB_4 store key. Forwards the value, or an updater for it, to the report store change strip data action bound to that key.
   *
   * @param v - The strip event changes to restore, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setStripEventChangesStateDEVICE = (v: Parameters<typeof setChangeStripData>[1]) =>
    setChangeStripData(KEY_RECOIL.DEVICE_MANAGEMENT, v);
  const setUpdatedDailySummaries = useReportStore((s) => s.actions.setUpdatedDailySummaries);
  const setEcgBookmarks = useReportStore((s) => s.actions.setEcgBookmarks);
  const setEcgBookmarkDeleteIds = useReportStore((s) => s.actions.setEcgBookmarkDeleteIds);
  const setBulkBeatsChangesAction = useReportStore((s) => s.actions.setBulkBeatsChanges);
  /**
   * Restores the bulk beat changes recorded under the TAB_1 store key. Forwards the value, or an updater for it, to the report store bulk beats changes action bound to that key.
   *
   * @param v - The bulk beat changes to restore, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setBulkBeatsChanges = (v: Parameters<typeof setBulkBeatsChangesAction>[1]) =>
    setBulkBeatsChangesAction(KEY_RECOIL.BEAT_HR, v);
  const setResetBulkBeatsChangesAction = useReportStore((s) => s.actions.setResetBulkBeatsChanges);
  /**
   * Restores the reset markers for bulk beat changes recorded under the TAB_1 store key. Forwards the value, or an updater for it, to the report store reset bulk beats changes action bound to that key.
   *
   * @param v - The reset markers to restore, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setResetBulkBeatsChanges = (v: Parameters<typeof setResetBulkBeatsChangesAction>[1]) =>
    setResetBulkBeatsChangesAction(KEY_RECOIL.BEAT_HR, v);
  const setBeatOptionsAction = useReportStore((s) => s.actions.setBeatOptions);
  /**
   * Restores the whole beat options snapshot recorded under the TAB_1 store key. Forwards the value, or an updater for it, to the report store beat options action bound to that key.
   *
   * @param v - The beat options snapshot to restore, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setBeatOptions = (v: Parameters<typeof setBeatOptionsAction>[1]) =>
    setBeatOptionsAction(KEY_RECOIL.BEAT_HR, v);

  const setActiveTabAction = useReportStore((s) => s.actions.setActiveTab);
  /**
   * Restores the active child tab recorded under the TAB_1 store key. Forwards the value, or an updater for it, to the report store active tab action bound to that key.
   *
   * @param v - The child tab to activate, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setActiveChildTab1 = (v: Parameters<typeof setActiveTabAction>[1]) =>
    setActiveTabAction(KEY_RECOIL.BEAT_HR, v);
  /**
   * Restores the active child tab recorded under the TAB_2 store key. Forwards the value, or an updater for it, to the report store active tab action bound to that key.
   *
   * @param v - The child tab to activate, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setActiveChildTab2 = (v: Parameters<typeof setActiveTabAction>[1]) =>
    setActiveTabAction(KEY_RECOIL.RHYTHM_EVENT, v);

  const setBulkReviewedBeats = useReportStore((s) => s.actions.setBulkReviewedBeats);
  const setResetBulkReviewedBeats = useReportStore((s) => s.actions.setResetBulkReviewedBeats);
  const setReviewedBeatsChanges = useReportStore((s) => s.actions.setReviewedBeatsChanges);
  const setSwapBeatChangesAction = useReportStore((s) => s.actions.setSwapBeatChanges);
  /**
   * Restores the swapped beat entries recorded under the TAB_1 store key. Forwards the value, or an updater for it, to the report store swap beat changes action bound to that key.
   *
   * @param v - The swapped beat entries to restore, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setSwapBeatChanges = (v: Parameters<typeof setSwapBeatChangesAction>[1]) =>
    setSwapBeatChangesAction(KEY_RECOIL.BEAT_HR, v);
  const setBulkSwapBeatsAction = useReportStore((s) => s.actions.setBulkSwapBeats);
  /**
   * Restores the bulk swap beat entries recorded under the TAB_1 store key. Forwards the value, or an updater for it, to the report store bulk swap beats action bound to that key.
   *
   * @param v - The bulk swap beat entries to restore, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setBulkSwapBeats = (v: Parameters<typeof setBulkSwapBeatsAction>[1]) =>
    setBulkSwapBeatsAction(KEY_RECOIL.BEAT_HR, v);
  const setResetSwapBeatsAction = useReportStore((s) => s.actions.setResetSwapBeats);
  /**
   * Restores the reset markers for swapped beats recorded under the TAB_1 store key. Forwards the value, or an updater for it, to the report store reset swap beats action bound to that key.
   *
   * @param v - The reset markers to restore, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setResetSwapBeats = (v: Parameters<typeof setResetSwapBeatsAction>[1]) =>
    setResetSwapBeatsAction(KEY_RECOIL.BEAT_HR, v);
  const setTemplateChangesAction = useReportStore((s) => s.actions.setTemplateChanges);
  /**
   * Restores the beat template changes recorded under the TAB_1 store key. Forwards the value, or an updater for it, to the report store template changes action bound to that key.
   *
   * @param v - The template changes to restore, or an updater receiving the current ones.
   * @returns Nothing.
   */
  const setTemplateChanges = (v: Parameters<typeof setTemplateChangesAction>[1]) =>
    setTemplateChangesAction(KEY_RECOIL.BEAT_HR, v);

  // Function to map key to corresponding Recoil state update
  /**
   * Writes one undo or redo history entry back into the report store slices it came from. Restores the active child tab from the recorded position, then switches on the entry key to fan the value out to the event, beat, grouped event, strip, ECG disclosure, template, daily summary and bookmark setters for the matching tab, clearing the undo-redo flag after 500 ms.
   *
   * @param key - The history entry key naming which state slice the value belongs to.
   * @param val - The recorded history value, whose valueOther holds the state and positions holds the tab to restore.
   * @param type - Optional context carrying typeTab, the tab the entry belongs to, and typeAction, undo or redo.
   * @returns Nothing.
   */
  const mapKeyToState = (
    key: UndoHistoryEntry['key'],
    val: UndoHistoryEntry['value'],
    type: { typeTab?: string | number; typeAction?: string } = {},
  ) => {
    const value = val?.valueOther;
    const position = val?.positions?.activeTabChild;
    if (type.typeTab === KEY_RECOIL.BEAT_HR || val?.positions?.activeTab === TAB_KEY.BEATS) {
      if (position !== undefined) {
        setActiveChildTab1(position);
      }
    } else if (
      type.typeTab === KEY_RECOIL.RHYTHM_EVENT ||
      val?.positions?.activeTab === TAB_KEY.ECG_EVENTS
    ) {
      if (position !== undefined) {
        setActiveChildTab2(position);
      }
    }
    switch (key) {
      case 'eventOptionsState':
        if (type.typeTab === KEY_RECOIL.BEAT_HR) {
          setEventNewsStateTab1(value?.eventNewsState || []);
          setEventChangesStateTab1(value?.eventChangesState || []);
          setEventOthersStateTab1(value?.eventOthersState || []);
        } else if (type.typeTab === KEY_RECOIL.RHYTHM_EVENT) {
          setEventNewsStateTab2(value?.eventNewsState || []);
          setEventChangesStateTab2(value?.eventChangesState || []);
          setEventOthersStateTab2(value?.eventOthersState || []);
        } else if (type.typeTab === KEY_RECOIL.HR_SUMMARY) {
          setEventNewsStateTab6(value?.eventNewsState || []);
          setEventChangesStateTab6(value?.eventChangesState || []);
          setEventOthersStateTab6(value?.eventOthersState || []);
        }
        break;
      case 'beatOptionsState':
        if (type.typeTab === KEY_RECOIL.BEAT_HR) {
          setBeatChangesStateTab1(value.beatChangesState || []);
          setBulkBeatsChanges(value.bulkBeatsChangesState || []);
          setResetBulkBeatsChanges(value.resetBulkBeatsChangesState || []);
          setSwapBeatChanges(value.swapBeatChangesState || []);
          setBulkSwapBeats(value.bulkSwapBeatsState || []);
          setResetSwapBeats(value.resetSwapBeatsState || []);
          setReviewedBeatsChanges(value.reviewedBeatsChangesState || []);
          setBulkReviewedBeats(value.bulkReviewedBeatsState || []);
          setResetBulkReviewedBeats(value.resetBulkReviewedBeatsState || []);
          setBeatOptions(value || defaultBeatsOptions);
        } else if (type.typeTab === KEY_RECOIL.RHYTHM_EVENT) {
          setBeatChangesStateTab2(value.beatChangesState || []);
        } else if (type.typeTab === KEY_RECOIL.HR_SUMMARY) {
          setBeatChangesStateTab6(value.beatChangesState || []);
        }
        break;
      case 'groupEventChangesState':
        if (type.typeTab === KEY_RECOIL.BEAT_HR) setGroupEventChanges1(value || []);
        else if (type.typeTab === KEY_RECOIL.RHYTHM_EVENT) setGroupEventChanges2(value || []);
        break;
      case 'changeStripDataState':
        // Case for Strip Event Changes
        if (type.typeTab === KEY_RECOIL.STRIP_MANAGEMENT) setStripEventChangesStateAI(value || []);
        else if (type.typeTab === KEY_RECOIL.DEVICE_MANAGEMENT)
          setStripEventChangesStateDEVICE(value || []);
        break;
      case 'ecgDisclosureChangeState':
        setEcgDisclosureChange(value || undefined);
        break;
      case 'templateChangesState':
        if (type.typeTab === KEY_RECOIL.BEAT_HR) {
          setTemplateChanges(value || undefined);
        }
        break;
      case 'updatedDailySummariesState':
        setUpdatedDailySummaries(value || []);
        break;
      case 'ecgBookmarksState':
        setEcgBookmarks(value || []);
        break;
      case 'ecgBookmarkDeleteIdsState':
        setEcgBookmarkDeleteIds(value || []);
        break;
      default:
        break;
    }
    setTimeout(() => {
      setIsUndoRedo(false);
    }, 500);
  };

  /**
   * Reports whether a keydown target is a text-editing field the browser undoes natively. Recognizes input, textarea and contenteditable elements so their built-in undo/redo is left alone.
   *
   * @param target - The keydown event target.
   * @returns True when the target is an editable form field.
   */
  const isEditableTarget = (target: EventTarget | null) => {
    const el = target as HTMLElement | null;
    if (!el || el.nodeType !== 1) return false;
    return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable;
  };

  /**
   * Pops the previous history entry and replays it into the report store. Applies it through the state mapper, then broadcasts the new history position on UNDO_REDO_CURRENT_INDEX so the toolbar flags update from their single owner.
   *
   * @returns A promise resolving once the undone entry has been applied and the position broadcast.
   */
  const applyUndo = async () => {
    const undoData = (await handleUndoGroupedByKey()) as UndoHistoryEntry | null;
    if (undoData) {
      setIsUndoRedo(true);
      mapKeyToState(undoData.key, undoData.value, { typeTab: undoData.type, typeAction: 'undo' });
      emitter.emit(EMITTER_CONSTANTS.UNDO_REDO_CURRENT_INDEX, {
        currentIndex: undoData.index,
        maxIndex: undoData.maxIndex,
      });
    }
  };

  /**
   * Pops the next history entry and replays it into the report store. Applies it through the state mapper, then broadcasts the new history position on UNDO_REDO_CURRENT_INDEX so the toolbar flags update from their single owner.
   *
   * @returns A promise resolving once the redone entry has been applied and the position broadcast.
   */
  const applyRedo = async () => {
    const redoData = (await handleRedoGroupedByKey()) as UndoHistoryEntry | null;
    if (redoData) {
      setIsUndoRedo(true);
      mapKeyToState(redoData.key, redoData.value, { typeTab: redoData.type, typeAction: 'redo' });
      emitter.emit(EMITTER_CONSTANTS.UNDO_REDO_CURRENT_INDEX, {
        currentIndex: redoData.index,
        maxIndex: redoData.maxIndex,
      });
    }
  };

  useEffect(() => {
    const runUndoRedo = debounce((mode: 'undo' | 'redo') => {
      if (mode === 'undo') applyUndo();
      else applyRedo();
    }, 150);
    const handleKeyDown = (event: KeyboardEvent) => {
      const { keyCode, ctrlKey, shiftKey, metaKey } = event;
      if (!(ctrlKey || metaKey)) return;
      const isUndo = keyCode === 90 && !shiftKey;
      const isRedo = keyCode === 89 || (keyCode === 90 && shiftKey);
      if (!isUndo && !isRedo) return;
      // Leave the browser's native undo/redo to text fields; only preventDefault must run
      // synchronously here — deferring it into the debounce (as before) prevented nothing.
      if (isEditableTarget(event.target)) return;
      event.preventDefault();
      runUndoRedo(isUndo ? 'undo' : 'redo');
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      runUndoRedo.cancel();
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);
};

export default useUndoRedoListener;
